import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, ShieldCheck, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { companyInfo } from "../data";
import { fetchPageBySlug } from "../lib/cms";

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Formular-tilstande
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "" // Honeypot felt
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug("kontakt");
        setPageData(page);
      } catch (error) {
        console.error("Fejl ved indlæsning af kontakt data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Validering
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Navn er påkrævet";
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer er påkrævet";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ugyldig emailadresse";
    }
    if (!formData.description.trim()) newErrors.description = "Besked er påkrævet";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Fjern fejlbesked, når brugeren skriver
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error("Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }

      setSubmitSuccess(true);
      // Nulstil formular
      setFormData({
        name: "",
        phone: "",
        email: "",
        description: "",
        website_url: ""
      });
    } catch (error) {
      setSubmitError(error.message || "Kunne ikke oprette forbindelse til serveren.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/9738536a-032a-47c0-a7f4-0f666e2d73c6.jpg"
            alt="Kontakt os"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Kontakt os"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Har du spørgsmål, eller ønsker du et uforpligtende tilbud? Skriv eller ring til os. Vi står altid klar til at hjælpe dig.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-5 text-left space-y-8">
              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-1/2" />
                  <div className="h-4 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-600">
                      Lokalt autoværksted
                    </p>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 sm:text-3xl">
                      BRDR. LORENZEN. SKÆRBÆK ApS
                    </h2>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 space-y-6 shadow-sm">
                    <ul className="space-y-4 text-sm">
                      <li className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 shrink-0 text-blue-500" />
                        <div>
                          <p className="text-slate-900 font-semibold">{companyInfo.address}</p>
                          <p className="text-slate-600 mt-0.5">{companyInfo.zipCity}</p>
                        </div>
                      </li>
                      <li className="flex items-center gap-3">
                        <Phone className="h-5 w-5 shrink-0 text-blue-500" />
                        <a
                          href={`tel:${companyInfo.phone}`}
                          className="text-slate-900 hover:text-blue-600 transition-colors font-mono font-semibold"
                        >
                          {companyInfo.phoneFormatted}
                        </a>
                      </li>
                      <li className="flex items-center gap-3">
                        <Mail className="h-5 w-5 shrink-0 text-blue-500" />
                        <a
                          href={`mailto:${companyInfo.email}`}
                          className="text-slate-900 hover:text-blue-600 transition-colors font-semibold"
                        >
                          {companyInfo.email}
                        </a>
                      </li>
                      <li className="flex items-start gap-3 pt-4 border-t border-slate-200">
                        <Clock className="h-5 w-5 shrink-0 text-blue-500" />
                        <div className="space-y-1.5 w-full">
                          {companyInfo.openingHours.map((item) => (
                            <div key={item.days} className="flex justify-between text-xs">
                              <span className="text-slate-600">{item.days}</span>
                              <span className="text-slate-900 font-medium font-mono">{item.hours}</span>
                            </div>
                          ))}
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-start gap-3 text-xs text-slate-500">
                    <ShieldCheck className="h-5 w-5 text-blue-500 shrink-0" />
                    <p className="leading-relaxed">
                      CVR: {companyInfo.cvr} · Medlem af Dansk Bilbrancheråd (DBR). Din sikkerhed for professionelt udført arbejde.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Right Column: Form */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-10 text-left shadow-lg">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-6">
                  Send os en besked
                </h3>

                {submitSuccess ? (
                  <div className="py-8 text-center space-y-6 animate-in fade-in duration-300">
                    <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
                    <div className="space-y-2">
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Beskeden er sendt!</h3>
                      <p className="text-slate-600 max-w-md mx-auto text-sm leading-relaxed">
                        Tak for din henvendelse. Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
                      </p>
                    </div>
                    <button
                      onClick={() => setSubmitSuccess(false)}
                      className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitError && (
                      <div className="flex items-start gap-3 rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-600">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p>{submitError}</p>
                      </div>
                    )}

                    {/* Antispam Honeypot (Skjult for mennesker) */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Navn */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Navn <span className="text-blue-600">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                          errors.name ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                        }`}
                        placeholder="Dit fulde navn"
                      />
                      {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {/* Telefonnummer */}
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Telefonnummer <span className="text-blue-600">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.phone ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                          }`}
                          placeholder="F.eks. 74751417"
                        />
                        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Email <span className="text-blue-600">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.email ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                          }`}
                          placeholder="din@email.dk"
                        />
                        {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Besked */}
                    <div className="space-y-2">
                      <label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Besked <span className="text-blue-600">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows="5"
                        value={formData.description}
                        onChange={handleChange}
                        className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                          errors.description ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                        }`}
                        placeholder="Skriv din besked her..."
                      />
                      {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-4 text-base font-bold text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/10 hover:shadow-blue-600/20"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sender besked...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Send Besked
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[450px] w-full border-t border-slate-200 relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2279.775787060434!2d8.764332815917409!3d55.16030358039809!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b4e23b6917d6fd%3A0x4110a82dd9a3e6e7!2sRibevej%2026%2C%206780%20Sk%C3%A6rb%C3%A6k!5e0!3m2!1sda!2sdk!4v1653648000000!5m2!1sda!2sdk"
          width="100%"
          height="100%"
          style={{ border: 0, filter: "grayscale(0.2) contrast(1)" }}
          allowFullScreen=""
          loading="lazy"
          title="BRDR. LORENZEN. SKÆRBÆK ApS på kort"
        ></iframe>
      </section>
    </div>
  );
}
